/**
 * Shared helpers for the Freight operations modules (A.15: dispatch, claims, credit, billing, AmFast, import).
 * Every screen is built from the design system (@flexfreight/ui): PageHeader, FilterToolbar + useUrlFilters,
 * DataTable, DetailLayout, StatusBadge. This file only holds formatting, capability checks and small links.
 */
import { format, usd, type ConsoleCapability } from "@flexfreight/core";
import type { SortState } from "@flexfreight/ui";
import { Link } from "react-router";
import { ApiError } from "../../api/client";
import { useConsoleMe } from "../../auth/context";

/** Integer cents to "$1,234.56" with the core money formatter (display only). */
export const money = (cents: number | null | undefined): string => (cents === null || cents === undefined ? "-" : format(usd(cents)));
export const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");
export const label = (s: string) => s.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

const DAY = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
const WHEN = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
/** "Oct 14, 2026" for a YYYY-MM-DD (never shifted by the browser's time zone) or an ISO instant. */
export const day = (d: string | null | undefined): string => (d ? DAY.format(new Date(d.length === 10 ? `${d}T12:00:00Z` : d)) : "-");
export const when = (iso: string | null | undefined): string => (iso ? WHEN.format(new Date(iso)) : "-");
export const ageDays = (iso: string, now = Date.now()): number => Math.max(0, Math.floor((now - Date.parse(iso)) / 86_400_000));
/** Sortable instant for a date or ISO string (blank sorts last). */
export const time = (d: string | null | undefined): number | null => (d ? Date.parse(d.length === 10 ? `${d}T12:00:00Z` : d) : null);

/** The signed-in staff member's capability check (UI convenience; the API enforces every action). */
export function useCan(): (cap: ConsoleCapability) => boolean {
  const me = useConsoleMe();
  return (cap) => me.capabilities.includes(cap);
}

/** Sort state from URL filter values `sort` + `dir` (client-sorted lists keep their sort in the URL too). */
export const sortOf = (v: { sort: string; dir: string }): SortState => (v.sort ? { key: v.sort, dir: v.dir === "desc" ? "desc" : "asc" } : null);
export const sortPatch = (s: SortState) => ({ sort: s?.key ?? "", dir: s?.dir ?? "asc" });

/** Link to a customer's console page. */
export function CustomerLink({ orgId, name }: { orgId: string; name: string }) {
  return (
    <Link to={`/customers/${orgId}`} className="ffa-rowlink">
      {name || "Customer"}
    </Link>
  );
}

/** Back link for a freight detail page header. */
export function BackLink({ to, children }: { to: string; children: string }) {
  return (
    <Link to={to} className="ffa-back">
      ← {children}
    </Link>
  );
}

/** Opens a shipment document through the console's audited document route. */
export const documentHref = (shipmentId: string, documentId: string) => `/v1/console/shipments/${shipmentId}/documents/${documentId}`;
export const kb = (bytes: number) => `${Math.max(1, Math.round(bytes / 1024))} KB`;

/** Plain-language result of the customer email a decision triggers. */
export function emailOutcomeText(o: "queued" | "not_connected" | "not_sent"): string | undefined {
  if (o === "queued") return "The customer is being emailed.";
  if (o === "not_connected") return "Email isn't connected, so the customer wasn't emailed. They see the update in their portal.";
  return undefined;
}
